/** @jsxImportSource theme-ui */
import { FormattedText } from "@bottlebooks/gatsby-design-system";
import { Trans } from "@lingui/macro";
import { FragmentType, graphql, useFragment } from "~/gql";
import Section from "../Section/Section";

const fragment = graphql(`
  fragment OldVineProjectVineyardFields on Product {
    plantDates: customFieldValue(key: "plantDate") {
      __typename
      ... on MultiSelectFieldValue {
        values {
          value
        }
      }
    }
    __typename
    ... on Wine {
      vineyardElevation
    }
    uniqueStoryOverview: customFieldValue(
      key: "uniqueStoryOverviewOrSellingPointsAboutThisWine50WordsMax"
    ) {
      __typename
      ... on TextFieldValue {
        value
      }
    }
    howAreTheseVineyardsTrellised: customFieldValue(
      key: "howAreTheseVineyardsTrellised"
    ) {
      __typename
      ... on MultiSelectFieldValue {
        values {
          value
        }
      }
    }
    areTheseVineyardsIrrigated: customFieldValue(
      key: "areTheseVineyardsIrrigated"
    ) {
      __typename
      ... on SelectFieldValue {
        value
      }
    }
    plantDatesPerVarietal: customFieldValue(
      key: "plantDatesPerVarietalPleaseList"
    ) {
      __typename
      ... on TextFieldValue {
        value
      }
    }
    hectaresPerVarietal: customFieldValue(
      key: "hectaresPerVarietalPleaseList"
    ) {
      __typename
      ... on TextFieldValue {
        value
      }
    }
    interplantingPercentage: customFieldValue(
      key: "interplantingPercentageRoughYoungVinesOfTotalVineyard"
    ) {
      __typename
      ... on TextFieldValue {
        value
      }
    }
    tonnagePerHectarePerVarietal: customFieldValue(
      key: "tonnagePerHectarePerVarietalPleaseList"
    ) {
      __typename
      ... on TextFieldValue {
        value
      }
    }
    rootstockPerVarietal: customFieldValue(
      key: "rootstockPerVarietalPleaseList"
    ) {
      __typename
      ... on TextFieldValue {
        value
      }
    }
  }
`);

export default function OldVineProjectVineyardCuelds({
  data,
}: {
  data: FragmentType<typeof fragment>;
}) {
  const product = useData(data);
  if (!product) return null;

  return (
    <>
      {product.plantDates ? (
        <Section title={<Trans>Plant dates</Trans>}>
          <FormattedText
            text={product.plantDates?.join(", ")}
            variant="large"
          />
        </Section>
      ) : null}
      {product.vineyardElevation ? (
        <Section title={<Trans>Elevation</Trans>}>
          <FormattedText text={product.vineyardElevation} variant="large" />
        </Section>
      ) : null}
      {product.howAreTheseVineyardsTrellised?.length ? (
        <Section title={<Trans>How are these vineyards trellised?</Trans>}>
          <FormattedText
            text={product.howAreTheseVineyardsTrellised?.join(", ")}
            variant="large"
          />
        </Section>
      ) : null}
      {product.areTheseVineyardsIrrigated ? (
        <Section title={<Trans>Are these vineyards irrigated?</Trans>}>
          <FormattedText
            text={product.areTheseVineyardsIrrigated}
            variant="large"
          />
        </Section>
      ) : null}
      {product.plantDatesPerVarietal ? (
        <Section title={<Trans>Plant dates per varietal</Trans>}>
          <FormattedText text={product.plantDatesPerVarietal} variant="large" />
        </Section>
      ) : null}
      {product.hectaresPerVarietal ? (
        <Section title={<Trans>Hectares per varietal</Trans>}>
          <FormattedText text={product.hectaresPerVarietal} variant="large" />
        </Section>
      ) : null}
      {product.interplantingPercentage ? (
        <Section
          title={
            <Trans>
              Interplanting percentage (rough % young vines of total vineyard)
            </Trans>
          }
        >
          <FormattedText
            text={product.interplantingPercentage}
            variant="large"
          />
        </Section>
      ) : null}
      {product.tonnagePerHectarePerVarietal ? (
        <Section title={<Trans>Tonnage per hectare per varietal</Trans>}>
          <FormattedText
            text={product.tonnagePerHectarePerVarietal}
            variant="large"
          />
        </Section>
      ) : null}
      {product.rootstockPerVarietal ? (
        <Section title={<Trans>Rootstock per varietal</Trans>}>
          <FormattedText text={product.rootstockPerVarietal} variant="large" />
        </Section>
      ) : null}
      {product.uniqueStoryOverview ? (
        <Section
          title={
            <Trans>
              Unique story, overview or selling points about this wine
            </Trans>
          }
        >
          <FormattedText text={product.uniqueStoryOverview} variant="large" />
        </Section>
      ) : null}
    </>
  );
}

function useData(data: FragmentType<typeof fragment>) {
  const product = useFragment(fragment, data);
  if (!product) return;
  const values = {
    plantDates:
      product.plantDates?.__typename === "MultiSelectFieldValue"
        ? product.plantDates.values?.flatMap(({ value }) =>
            value === "notSelected" ? [] : value ?? []
          )
        : undefined,
    vineyardElevation:
      product.__typename === "Wine" ? product.vineyardElevation : undefined,
    howAreTheseVineyardsTrellised:
      product.howAreTheseVineyardsTrellised?.__typename ===
      "MultiSelectFieldValue"
        ? product.howAreTheseVineyardsTrellised.values?.flatMap(({ value }) =>
            value === "notSelected" ? [] : value ?? []
          )
        : undefined,
    areTheseVineyardsIrrigated:
      product.areTheseVineyardsIrrigated?.__typename === "SelectFieldValue"
        ? product.areTheseVineyardsIrrigated.value
        : undefined,
    plantDatesPerVarietal:
      product.plantDatesPerVarietal?.__typename === "TextFieldValue"
        ? product.plantDatesPerVarietal.value
        : undefined,
    hectaresPerVarietal:
      product.hectaresPerVarietal?.__typename === "TextFieldValue"
        ? product.hectaresPerVarietal.value
        : undefined,
    interplantingPercentage:
      product.interplantingPercentage?.__typename === "TextFieldValue"
        ? product.interplantingPercentage.value
        : undefined,
    tonnagePerHectarePerVarietal:
      product.tonnagePerHectarePerVarietal?.__typename === "TextFieldValue"
        ? product.tonnagePerHectarePerVarietal.value
        : undefined,
    rootstockPerVarietal:
      product.rootstockPerVarietal?.__typename === "TextFieldValue"
        ? product.rootstockPerVarietal.value
        : undefined,
    uniqueStoryOverview:
      product.uniqueStoryOverview?.__typename === "TextFieldValue"
        ? product.uniqueStoryOverview.value
        : undefined,
  } as const;
  if (
    Object.values(values).filter((value) => {
      if (value == null) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }).length === 0
  )
    return;
  return values;
}
